import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Mail, Phone, MapPin, Send, Check,
  MessageSquare, Clock, Instagram, Facebook, Twitter
} from "lucide-react";
import Navbar from "../../components/Header";

const G      = "var(--k-green)";
const G_DARK = "var(--k-green-hover)";
const G_LIGHT = "var(--k-green-soft)";
const API    = "https://api.komorotel.com";

const CONTACT_INFO = [
  {
    icon: <Mail size={20} color={G} />,
    label: "Email",
    value: "contact@komorotel.com",
    sub: "Réponse sous 24h",
    href: "mailto:contact@komorotel.com",
  },
  {
    icon: <Phone size={20} color={G} />,
    label: "Téléphone",
    value: "+269 77 00 000",
    sub: "Lun–Ven, 8h–17h",
    href: "tel:+26977000000",
  },
  {
    icon: <MapPin size={20} color={G} />,
    label: "Adresse",
    value: "Moroni, Grande Comore",
    sub: "Comores, Océan Indien",
    href: null,
  },
  {
    icon: <Clock size={20} color={G} />,
    label: "Disponibilité",
    value: "7j/7 en ligne",
    sub: "Support client continu",
    href: null,
  },
];

const FAQ = [
  {
    q: "Comment annuler une réservation ?",
    a: "Rendez-vous dans 'Mes réservations' et cliquez sur 'Demander une annulation'. Les conditions dépendent du tarif choisi.",
  },
  {
    q: "Je suis hôtelier, comment rejoindre Komorotel ?",
    a: "Cliquez sur 'Proposer mon établissement' depuis la page d'accueil ou contactez-nous directement par email.",
  },
  {
    q: "Le paiement est-il sécurisé ?",
    a: "Oui, toutes les transactions sont sécurisées. Aucun paiement n'est prélevé avant votre confirmation.",
  },
  {
    q: "Puis-je modifier mes dates après réservation ?",
    a: "Contactez l'établissement directement ou notre support. Les modifications dépendent de la disponibilité.",
  },
];

export default function Contact() {
  const [form,      setForm]      = useState({ name: "", email: "", subject: "", message: "" });
  const [errors,    setErrors]    = useState({});
  const [loading,   setLoading]   = useState(false);
  const [sent,      setSent]      = useState(false);
  const [focused,   setFocused]   = useState(null);
  const [openFaq,   setOpenFaq]   = useState(null);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Requis";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Email invalide";
    if (!form.subject.trim()) e.subject = "Requis";
    if (!form.message.trim()) e.message = "Requis";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    // Simulé pour l'instant — brancher sur ton endpoint email si besoin
    await new Promise(r => setTimeout(r, 1200));
    setSent(true);
    setLoading(false);
  };

  const inputStyle = (name) => ({
    width: "100%", border: `1.5px solid ${errors[name] ? "#ef4444" : focused === name ? G : "#e5e7eb"}`,
    borderRadius: 12, padding: "12px 14px", fontSize: 14, outline: "none",
    fontFamily: "inherit", color: "#111827", background: "#fff",
    boxSizing: "border-box", transition: "border-color 0.2s",
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <Navbar />

      {/* ── Hero ── */}
      <div style={{ background: "linear-gradient(135deg, #0a2810 0%, #0f4020 100%)", paddingTop: 96, paddingBottom: 56 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", background: "rgba(58,157,35,0.25)", border: "1px solid rgba(58,157,35,0.4)", color: G, fontSize: 11, fontWeight: 800, letterSpacing: "0.09em", padding: "5px 14px", borderRadius: 20, marginBottom: 20 }}>
            💬 NOUS CONTACTER
          </div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 42, fontWeight: 700, color: "#fff", lineHeight: 1.2, marginBottom: 14 }}>
            On est là pour vous aider
          </h1>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.7)", maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
            Une question, un problème ou vous souhaitez rejoindre Komorotel ? Écrivez-nous.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 32px 64px" }}>
        <div className="contact-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 28, alignItems: "start" }}>

          {/* ── Colonne gauche ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Infos de contact */}
            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 20 }}>
                Informations de contact
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {CONTACT_INFO.map((c, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 12, background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {c.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>{c.label}</div>
                      {c.href ? (
                        <a href={c.href} style={{ fontSize: 14, fontWeight: 600, color: "#111827", textDecoration: "none" }}
                          onMouseEnter={e => e.currentTarget.style.color = G}
                          onMouseLeave={e => e.currentTarget.style.color = "#111827"}>
                          {c.value}
                        </a>
                      ) : (
                        <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{c.value}</div>
                      )}
                      <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>{c.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Réseaux sociaux */}
              <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 22, paddingTop: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Réseaux sociaux</div>
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    { icon: <Facebook size={15} />, href: "#", label: "Facebook" },
                    { icon: <Instagram size={15} />, href: "#", label: "Instagram" },
                    { icon: <Twitter size={15} />, href: "#", label: "Twitter" },
                  ].map((s, i) => (
                    <a key={i} href={s.href}
                      style={{ display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1px solid #f3f4f6", borderRadius: 9, padding: "8px 12px", fontSize: 12, fontWeight: 600, color: "#374151", textDecoration: "none", transition: "all 0.18s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = G_LIGHT; e.currentTarget.style.color = G; e.currentTarget.style.borderColor = `rgb(var(--k-green-rgb)/0.25)`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.color = "#374151"; e.currentTarget.style.borderColor = "#f3f4f6"; }}>
                      {s.icon}{s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 18 }}>
                Questions fréquentes
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {FAQ.map((faq, i) => (
                  <div key={i} style={{ border: "1px solid #f3f4f6", borderRadius: 12, overflow: "hidden" }}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", background: openFaq === i ? G_LIGHT : "#fff", border: "none", cursor: "pointer", textAlign: "left", transition: "background 0.18s" }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: openFaq === i ? G : "#111827" }}>{faq.q}</span>
                      <span style={{ color: openFaq === i ? G : "#9ca3af", fontSize: 16, fontWeight: 700, transition: "transform 0.2s", transform: openFaq === i ? "rotate(45deg)" : "none", flexShrink: 0, marginLeft: 8 }}>+</span>
                    </button>
                    {openFaq === i && (
                      <div style={{ padding: "12px 16px 14px", fontSize: 13, color: "#374151", lineHeight: 1.65, background: "#fafafa", borderTop: "1px solid #f3f4f6" }}>
                        {faq.a}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Colonne droite — Formulaire ── */}
          <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #f3f4f6", padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>

            {sent ? (
              /* ── État succès ── */
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
                  <Check size={28} color={G} strokeWidth={2.5} />
                </div>
                <h3 style={{ fontFamily: "Georgia, serif", fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 10 }}>
                  Message envoyé !
                </h3>
                <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.7, marginBottom: 28, maxWidth: 340 }}>
                  Merci pour votre message. Notre équipe vous répondra dans les 24 heures.
                </p>
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                  style={{ background: G_LIGHT, color: G, border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 26 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <MessageSquare size={18} color={G} />
                  </div>
                  <div>
                    <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 700, color: "#111827" }}>Envoyez-nous un message</h2>
                    <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Réponse garantie sous 24h</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                  {/* Nom + Email */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {[{ k: "name", ph: "Votre nom *" }, { k: "email", ph: "Votre email *" }].map(f => (
                      <div key={f.k}>
                        <input type={f.k === "email" ? "email" : "text"}
                          value={form[f.k]} onChange={e => set(f.k, e.target.value)}
                          placeholder={f.ph} style={inputStyle(f.k)}
                          onFocus={() => setFocused(f.k)} onBlur={() => setFocused(null)} />
                        {errors[f.k] && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors[f.k]}</div>}
                      </div>
                    ))}
                  </div>

                  {/* Sujet */}
                  <div>
                    <select value={form.subject} onChange={e => set("subject", e.target.value)}
                      style={{ ...inputStyle("subject"), color: form.subject ? "#111827" : "#9ca3af" }}
                      onFocus={() => setFocused("subject")} onBlur={() => setFocused(null)}>
                      <option value="">Sujet de votre message *</option>
                      <option value="reservation">Question sur une réservation</option>
                      <option value="hotelier">Je suis hôtelier</option>
                      <option value="technique">Problème technique</option>
                      <option value="partenariat">Partenariat</option>
                      <option value="autre">Autre</option>
                    </select>
                    {errors.subject && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors.subject}</div>}
                  </div>

                  {/* Message */}
                  <div>
                    <textarea
                      value={form.message} onChange={e => set("message", e.target.value)}
                      placeholder="Votre message *"
                      rows={6}
                      style={{ ...inputStyle("message"), resize: "vertical", lineHeight: 1.6 }}
                      onFocus={() => setFocused("message")} onBlur={() => setFocused(null)}
                    />
                    {errors.message && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 3 }}>{errors.message}</div>}
                  </div>

                  {/* Bouton */}
                  <button type="submit" disabled={loading}
                    style={{
                      width: "100%", background: loading ? "#d1d5db" : G, color: "#fff",
                      border: "none", borderRadius: 12, padding: "14px 0", fontSize: 14,
                      fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                      transition: "background 0.18s", display: "flex", alignItems: "center",
                      justifyContent: "center", gap: 8,
                    }}
                    onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = G_DARK; }}
                    onMouseLeave={e => { if (!loading) e.currentTarget.style.backgroundColor = G; }}>
                    {loading ? "Envoi en cours..." : <><Send size={15} /> Envoyer le message</>}
                  </button>

                  <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                    En soumettant ce formulaire, vous acceptez notre{" "}
                    <Link to="/conditions" style={{ color: G, textDecoration: "none", fontWeight: 600 }}>politique de confidentialité</Link>.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}